import type {
  CreateProductDocumentTypeRequest,
  DeleteCheckResponse,
  FlowProductDocumentsData,
  ProductDocumentsOverviewData,
  ProductDocumentType,
  ProductDocumentTypeWithDocuments,
  SaveMsProductDocumentRequest,
  SaveUploadedProductDocumentRequest,
  SearchProductDocumentRequest,
  SearchProductDocumentResponse,
  UpdateProductDocumentTypeRequest,
} from "@repo/shared/types";
import { del, get, patch, post } from "./_client";

// Document types (settings)
export function getDocumentTypes(collectionId: string) {
  return get<ProductDocumentType[]>(
    `/internal/portfolio-collections/${collectionId}/document-types`,
  );
}

export function createDocumentType(collectionId: string, data: CreateProductDocumentTypeRequest) {
  return post<ProductDocumentType>(
    `/internal/portfolio-collections/${collectionId}/document-types`,
    data satisfies CreateProductDocumentTypeRequest,
  );
}

export function updateDocumentType(
  collectionId: string,
  typeId: string,
  data: UpdateProductDocumentTypeRequest,
) {
  return patch<ProductDocumentType>(
    `/internal/portfolio-collections/${collectionId}/document-types/${typeId}`,
    data satisfies UpdateProductDocumentTypeRequest,
  );
}

export function deleteDocumentType(collectionId: string, typeId: string) {
  return del(`/internal/portfolio-collections/${collectionId}/document-types/${typeId}`);
}

/** QL-168 — asked before the delete confirmation opens. */
export function checkDeleteDocumentType(collectionId: string, typeId: string) {
  return get<DeleteCheckResponse>(
    `/internal/portfolio-collections/${collectionId}/document-types/${typeId}/delete-check`,
  );
}

// Product documents (per-product)
export function getDocumentTypesWithDocs(collectionId: string, productId: string) {
  return get<ProductDocumentTypeWithDocuments[]>(
    `/internal/portfolio-collections/${collectionId}/products/${productId}/document-types-with-docs`,
  );
}

export function searchDocument(
  collectionId: string,
  productId: string,
  data: SearchProductDocumentRequest,
) {
  return post<SearchProductDocumentResponse>(
    `/internal/portfolio-collections/${collectionId}/products/${productId}/search-document`,
    data satisfies SearchProductDocumentRequest,
  );
}

export function saveMsDocument(
  collectionId: string,
  productId: string,
  data: SaveMsProductDocumentRequest,
) {
  return post(
    `/internal/portfolio-collections/${collectionId}/products/${productId}/save-ms-document`,
    data satisfies SaveMsProductDocumentRequest,
  );
}

export function saveUploadedDocument(
  collectionId: string,
  productId: string,
  data: SaveUploadedProductDocumentRequest,
) {
  return post(
    `/internal/portfolio-collections/${collectionId}/products/${productId}/save-uploaded-document`,
    data satisfies SaveUploadedProductDocumentRequest,
  );
}

export function deleteProductDocument(collectionId: string, productId: string, documentId: string) {
  return del(
    `/internal/portfolio-collections/${collectionId}/products/${productId}/document/${documentId}`,
  );
}

// Re-fetches the same Morningstar document using its stored parameters
export function refreshMsDocument(collectionId: string, productId: string, documentId: string) {
  return post(
    `/internal/portfolio-collections/${collectionId}/products/${productId}/document/${documentId}/refresh`,
  );
}

// Flow instance
export function getFlowInstanceProductDocuments(flowInstanceId: string) {
  return get<FlowProductDocumentsData | null>(
    `/internal/portfolio-collections/flow-instance/${flowInstanceId}/product-documents`,
  );
}

// Collection-wide overview (per-product, per-language documents in a portfolio collection)
export function getCollectionProductDocuments(collectionId: string, params?: { search?: string }) {
  const search = params?.search?.trim();
  const qs = search ? `?search=${encodeURIComponent(search)}` : "";
  return get<ProductDocumentsOverviewData>(
    `/internal/portfolio-collections/${collectionId}/product-documents-overview${qs}`,
  );
}
